import { useCallback, useMemo } from 'react';
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import type { LineChartOptions } from '../types';
import {
  isDimmed,
  makeSeriesClickHandler,
  markOpacity,
  useSeriesKeyboardSelection,
} from './chartSelection';
import { CartesianLegend, ChartCartesianGrid, ChartTooltip } from './parts';
import {
  AXIS_TICK_FONT_SIZE,
  CARTESIAN_CHART_MARGIN,
  type CartesianChartCaseProps,
  categoryAxisHeight,
  CHART_SERIES_ANIMATION,
  curveTypeForLineStyle,
  formatAxisValue,
  mapScale,
  resolveCssColor,
  valueAxisProps,
  xAxisLabelProps,
} from './shared';

/** Static cursor style for an interactive chart — hoisted since it never
 * depends on props. */
const POINTER_CURSOR_STYLE = { cursor: 'pointer' } as const;

/**
 * LineChartCase component - extracted to keep chart-type rendering logic out
 * of the switch statement in ChartRendererCore (mirrors PieChartCase above).
 * Solid-line rendering only — a non-'none' areaFill routes to
 * AreaChartCase instead (decided by the caller).
 */
export function LineChartCase({
  chartData,
  config,
  columnTitles,
  height,
  legendAlign,
  tooltipFormatter,
  seriesKeys,
  seriesColors,
  axisMeta,
  categoryBottomLabel,
  valueAxisTitle,
  showXAxisLabels = true,
  yAxisWidth,
  renderLegend = true,
  syncId,
  lineOptions,
  onDatapointSelect,
  activeSelection,
}: CartesianChartCaseProps & { lineOptions: LineChartOptions }) {
  const xAxisHeight = categoryAxisHeight({
    hasTitle: Boolean(categoryBottomLabel),
  });

  // Stable click handler factory — each Line gets its own key-scoped handler.
  const makeClickHandler = useCallback(
    (seriesKey: string) => makeSeriesClickHandler(seriesKey, onDatapointSelect),
    [onDatapointSelect]
  );

  const { handleMouseMove, handleContainerKeyDown } =
    useSeriesKeyboardSelection({ chartData, seriesKeys, onDatapointSelect });

  const interactive = Boolean(onDatapointSelect);

  const xAxisTick = useMemo(
    () =>
      showXAxisLabels
        ? {
            fill: resolveCssColor('--color-text-secondary'),
            fontSize: AXIS_TICK_FONT_SIZE,
          }
        : false,
    [showXAxisLabels]
  );

  const lineChart = (
    <LineChart
      data={chartData}
      margin={CARTESIAN_CHART_MARGIN}
      syncId={syncId}
      style={interactive ? POINTER_CURSOR_STYLE : undefined}
      onMouseMove={interactive ? handleMouseMove : undefined}
    >
      <ChartCartesianGrid show={config.axes?.xAxis.showGrid} />
      <XAxis
        dataKey='x'
        hide={!config.axes?.xAxis.show}
        axisLine={false}
        tickLine={false}
        tick={xAxisTick}
        height={xAxisHeight}
        label={
          showXAxisLabels ? xAxisLabelProps(categoryBottomLabel) : undefined
        }
        angle={config.axes?.xAxis.rotation}
      />
      <YAxis
        {...valueAxisProps({
          axisMeta,
          axisConfig: config.axes?.yAxisLeft,
          label: valueAxisTitle,
          width: yAxisWidth,
          scale: mapScale(config.axes?.yAxisLeft.scale || 'linear'),
          tickFormatter: config.axes?.yAxisLeft?.unit
            ? (value: number) => formatAxisValue(value, config.axes?.yAxisLeft)
            : axisMeta.autoTickFormatter,
          ticks: axisMeta.smallIntegerTicks,
        })}
      />
      <ChartTooltip show={config.tooltip.show} formatter={tooltipFormatter} />
      <CartesianLegend
        show={config.legend.show && renderLegend}
        legend={config.legend}
        legendAlign={legendAlign}
      />
      {seriesKeys.map(key => {
        // Per-point dimming on a Line is impractical (recharts Line does not
        // accept Cell children). We dim at the series level instead: if
        // another series' row is active, this series' stroke goes to 0.35.
        // Clicking anywhere on the line reports the nearest row index.
        const seriesDimmed =
          activeSelection !== null &&
          activeSelection !== undefined &&
          isDimmed(activeSelection, activeSelection.rowIndex, key);
        return (
          <Line
            key={key}
            type={curveTypeForLineStyle(lineOptions.lineStyle)}
            dataKey={key}
            name={columnTitles[key] || key}
            stroke={seriesColors[key]}
            strokeOpacity={markOpacity(seriesDimmed)}
            strokeWidth={2}
            dot={
              lineOptions.showPoints
                ? {
                    fill: 'var(--color-bg-primary)',
                    stroke: seriesColors[key],
                    strokeWidth: 2,
                    r: 4,
                  }
                : false
            }
            activeDot={{
              fill: 'var(--color-bg-primary)',
              stroke: seriesColors[key],
              strokeWidth: 2,
              r: 5,
            }}
            onClick={makeClickHandler(key)}
            {...CHART_SERIES_ANIMATION}
          />
        );
      })}
    </LineChart>
  );

  if (interactive) {
    return (
      <div
        tabIndex={0}
        onKeyDown={handleContainerKeyDown}
        className='outline-none'
        aria-label='Interactive line chart — press Enter or Space to drill into the hovered point, or the first point if none has been hovered'
      >
        <ResponsiveContainer width='100%' height={height}>
          {lineChart}
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <ResponsiveContainer width='100%' height={height}>
      {lineChart}
    </ResponsiveContainer>
  );
}
